import { useCallback, useEffect, useState } from "react";
import { Copy, Link2, Loader2, Mail, ShieldCheck, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { avatarColor, avatarInitial, avatarLabel } from "@/components/app/Presence";

const Row = ({ p, right }) => (
  <div className="flex items-center gap-3 py-2">
    <span className="grid h-8 w-8 place-items-center rounded-full text-white text-sm font-bold shrink-0" style={{ background: avatarColor(p.email || p.name) }}>
      {p.picture ? <img src={p.picture} alt="" className="h-full w-full rounded-full object-cover" /> : avatarInitial(p)}
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium truncate">{avatarLabel(p)}</div>
      <div className="text-xs text-slate-500 truncate">{p.email}</div>
    </div>
    {right}
  </div>
);

export const MembersDialog = ({ project, onClose }) => {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");

  const load = useCallback(async () => {
    try { const r = await api.get(`/projects/${project.id}/members`); setData(r.data); } catch (e) { toast.error(errMsg(e)); }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const h = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);

  const invite = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/projects/${project.id}/invite`, { email: email.trim() });
      setEmail("");
      await load();
      if (r.email_sent) toast.success(`Convite enviado para ${r.email}`);
      else { navigator.clipboard?.writeText(r.invite_link); toast.success("Convite criado — link copiado (e-mail não configurado)"); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const copyLink = async () => {
    try { const { data: r } = await api.post(`/projects/${project.id}/invite-link`); setLink(r.url); navigator.clipboard?.writeText(r.url); toast.success("Link de convite copiado"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const removeMember = async (uid) => { try { await api.delete(`/projects/${project.id}/members/${uid}`); await load(); toast.success("Acesso removido"); } catch (e) { toast.error(errMsg(e)); } };
  const revoke = async (id) => { try { await api.delete(`/projects/${project.id}/invite/${id}`); await load(); } catch (e) { toast.error(errMsg(e)); } };

  const isOwner = data?.is_owner;
  return (
    <div data-testid="members-dialog" className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 backdrop-blur-sm p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="font-display font-semibold text-lg flex items-center gap-2"><UserPlus size={18} className="text-[var(--blue)]" /> Equipe</h2>
            <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[380px]">{project.name}</p>
          </div>
          <button data-testid="members-close" onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {isOwner && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <label className="flex-1 h-11 rounded-xl border border-slate-200 flex items-center gap-2 px-3 text-sm focus-within:border-[var(--blue)]">
                  <Mail size={15} className="text-slate-400" />
                  <input data-testid="invite-email-input" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && invite()} type="email" placeholder="Convidar por e-mail…" className="flex-1 outline-none bg-transparent" />
                </label>
                <button data-testid="invite-email-send" onClick={invite} disabled={busy || !email.trim()} className="btn btn-primary !py-2.5 !px-4 disabled:opacity-50">{busy ? <Loader2 size={16} className="animate-spin" /> : "Convidar"}</button>
              </div>
              <button data-testid="invite-copy-link" onClick={copyLink} className="w-full h-11 rounded-xl border border-dashed border-slate-300 flex items-center justify-center gap-2 text-sm font-medium text-slate-600 hover:border-[var(--blue)] hover:text-[var(--blue)]"><Link2 size={15} /> Copiar link de convite</button>
              {link && <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500"><span className="truncate flex-1">{link}</span><button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Copiado"); }}><Copy size={13} /></button></div>}
            </div>
          )}

          <div className="mt-5 space-y-1 divide-y divide-slate-100">
            {data?.owner && <Row p={data.owner} right={<span className="text-[11px] font-mono flex items-center gap-1 text-slate-500"><ShieldCheck size={13} /> dono</span>} />}
            {data?.members?.map((m) => (
              <Row key={m.user_id} p={m} right={isOwner ? <button data-testid={`member-remove-${m.user_id}`} onClick={() => removeMember(m.user_id)} title="Remover acesso" className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button> : <span className="text-[11px] font-mono text-slate-400">editor</span>} />
            ))}
            {data?.pending?.map((i) => (
              <div key={i.id} className="flex items-center gap-3 py-2">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-amber-100 text-amber-600 shrink-0"><Mail size={14} /></span>
                <div className="min-w-0 flex-1"><div className="text-sm truncate">{i.email}</div><div className="text-xs text-amber-600">convite pendente</div></div>
                {isOwner && <button data-testid={`invite-revoke-${i.id}`} onClick={() => revoke(i.id)} className="text-xs text-slate-500 hover:text-red-600">cancelar</button>}
              </div>
            ))}
          </div>
          {!data && <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-slate-300" /></div>}
        </div>
      </div>
    </div>
  );
};
